<!-- src/lib/components/TaskForm.svelte -->
<script lang="ts">
  import { api } from '../api.ts';
  import { pmStore } from '../stores/pm.svelte.ts';
  import type { Persona } from '../types.ts';

  let prompt = $state('');
  let submitting = $state(false);
  let error = $state<string | null>(null);
  let textareaEl: HTMLTextAreaElement | null = $state(null);

  let personas = $state<Persona[]>([]);
  let assigneeKind = $state<'persona' | 'human' | null>(null);
  let assigneeId = $state<string>('');

  $effect(() => {
    const wsId = pmStore.activeWorkspaceId;
    if (!wsId) { personas = []; return; }
    api<Persona[]>(`/api/v1/workspaces/${wsId}/personas`)
      .then(p => { personas = p; })
      .catch(() => {});
  });

  function focusComposer() {
    textareaEl?.focus();
    const pos = textareaEl?.value.length ?? 0;
    textareaEl?.setSelectionRange(pos, pos);
  }

  $effect(() => {
    window.addEventListener('agentboard:focus-task-form', focusComposer);
    return () => window.removeEventListener('agentboard:focus-task-form', focusComposer);
  });

  async function submit() {
    if (!prompt.trim()) return;
    submitting = true;
    error = null;
    try {
      await api('/api/v1/tasks', {
        method: 'POST',
        body: JSON.stringify({
          prompt: prompt.trim(),
          assignee_kind: assigneeKind,
          assignee_id: assigneeId || null,
        }),
      });
      prompt = '';
    } catch (e) {
      error = String(e);
    } finally {
      submitting = false;
    }
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
  }
</script>

<form onsubmit={(e) => { e.preventDefault(); submit(); }}>
  <textarea
    bind:this={textareaEl}
    bind:value={prompt}
    onkeydown={onKeydown}
    placeholder="Describe the task for the agent… (⌘+Enter to submit)"
    rows={4}
    disabled={submitting}
  ></textarea>

  <div class="form-field">
    <label class="field-label" for="assignee-kind-unassigned">Assign to</label>
    <div class="assign-row">
      <button
        id="assignee-kind-unassigned"
        type="button"
        class="assign-chip"
        class:active={assigneeKind === null}
        onclick={() => { assigneeKind = null; assigneeId = ''; }}
      >Unassigned</button>
      <button
        type="button"
        class="assign-chip"
        class:active={assigneeKind === 'human'}
        onclick={() => { assigneeKind = 'human'; }}
      >👤 Human</button>
      {#if personas.length > 0}
        <button
          type="button"
          class="assign-chip"
          class:active={assigneeKind === 'persona'}
          onclick={() => { assigneeKind = 'persona'; }}
        >🤖 Agent</button>
      {/if}
    </div>
    {#if assigneeKind === 'human'}
      <input
        class="field-input"
        type="text"
        bind:value={assigneeId}
        placeholder="Name or @handle"
      />
    {:else if assigneeKind === 'persona'}
      <select class="field-select" bind:value={assigneeId}>
        <option value="">Select persona…</option>
        {#each personas as p (p.id)}
          <option value={p.id}>{p.name}</option>
        {/each}
      </select>
    {/if}
  </div>

  {#if error}
    <p class="error">{error}</p>
  {/if}
  <button type="submit" disabled={submitting || !prompt.trim()}>
    {submitting ? 'Dispatching…' : 'Dispatch Agent'}
  </button>
</form>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  textarea {
    width: 100%;
    padding: 0.875rem;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.03);
    color: #e8eaf0;
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 0.875rem;
    resize: vertical;
    outline: none;
    transition: border-color 100ms ease;
    box-sizing: border-box;
  }
  textarea:focus {
    border-color: oklch(0.80 0.26 310);
    box-shadow: 0 0 0 1px oklch(0.80 0.26 310)40, inset 0 0 20px oklch(0.74 0.28 310 / 0.07);
  }
  textarea:disabled { opacity: 0.5; cursor: not-allowed; }
  button[type="submit"] {
    align-self: flex-end;
    padding: 0.625rem 1.5rem;
    background: linear-gradient(135deg, #0066cc, oklch(0.58 0.22 265));
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
    box-shadow: 0 0 12px oklch(0.55 0.20 265 / 0.30);
  }
  button[type="submit"]:hover:not(:disabled) {
    box-shadow: 0 0 20px oklch(0.55 0.20 265 / 0.60);
    transform: translateY(-1px);
  }
  button[type="submit"]:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; transform: none; }
  .error { color: oklch(0.78 0.16 85); font-size: 0.8rem; font-family: 'JetBrains Mono', monospace; }

  .form-field { display: flex; flex-direction: column; gap: 0.25rem; }
  .field-label { font-size: 0.75rem; color: oklch(0.65 0.04 275); font-weight: 500; }
  .field-input {
    width: 100%;
    background: oklch(0.14 0.02 275 / 0.6);
    border: 1px solid oklch(0.30 0.05 275 / 0.20);
    border-radius: 6px; color: oklch(0.90 0.05 310);
    font-size: 0.82rem; padding: 0.4rem 0.6rem;
    font-family: inherit; box-sizing: border-box;
  }
  .assign-row { display: flex; gap: 0.35rem; flex-wrap: wrap; margin-bottom: 0.4rem; }
  .assign-chip {
    background: oklch(0.14 0.02 275 / 0.6);
    border: 1px solid oklch(0.30 0.05 275 / 0.25);
    border-radius: 5px; color: oklch(0.75 0.05 310 / 0.8);
    font-size: 0.75rem; padding: 0.25rem 0.55rem; cursor: pointer;
    transition: background 100ms, border-color 100ms, color 100ms;
  }
  .assign-chip:hover { background: oklch(0.18 0.03 275); color: oklch(0.90 0.08 310); }
  .assign-chip.active {
    background: oklch(0.72 0.28 310 / 0.18);
    border-color: oklch(0.72 0.28 310 / 0.45);
    color: oklch(0.96 0.12 310);
  }
  .field-select {
    width: 100%;
    background: oklch(0.14 0.02 275 / 0.6);
    border: 1px solid oklch(0.30 0.05 275 / 0.20);
    border-radius: 6px; color: oklch(0.90 0.05 310);
    font-size: 0.82rem; padding: 0.4rem 0.6rem; cursor: pointer;
    appearance: none; font-family: inherit;
  }
</style>
